import { useMemo } from 'react';
import { Award, ChefHat, Flame, UserMinus } from 'lucide-react';
import {
  DataTable,
  DateRangeFilter,
  DocNote,
  ExportButton,
  FilterBar,
  FilterDivider,
  MetricCard,
  MetricRow,
  Pager,
  Select,
  SelectableChip,
  StatusPill,
  type DataTableColumn,
} from '../../components';
import { useSession } from '../../lib/auth/useSession';
import { formatCount } from '../../lib/format/money';
import { formatDate } from '../../lib/format/dates';
import { useTableUrlState } from '../../lib/table/tableSearch';
import { LocalePicker } from '../locales/LocalePicker';
import {
  BLUE_STAR_STATUSES,
  type AdminProfileRow,
  type DirectoryFilters,
  type NeverPublished,
  type ProfileSortKey,
} from '../profiles/api';
import { useExportProfiles, useNeverPublished, useProfileCount, useProfileDirectory } from '../profiles/queries';
import {
  BLUE_STAR_LABEL,
  BLUE_STAR_TONE,
  CHEF_STATUSES,
  CHEF_STATUS_LABEL,
  CHEF_STATUS_TONE,
  ProfileCell,
  RatingCell,
  dayRangeParam,
  listParam,
  numberParam,
  pickParam,
  stringParam,
} from '../profiles/people';
import { DetailDialogs } from '../orders/DetailDialogs';
import { CHEF_TABS, ChefDialog } from './ChefDialog';

const NO_FIRST_DISH = 'no-first-dish';
const RATINGS = ['1', '2', '3', '4', '5'];

const COLUMNS: DataTableColumn<AdminProfileRow>[] = [
  {
    id: 'chef',
    header: 'Chef',
    width: '1.9fr',
    sortKey: 'displayName',
    cell: (r) => <ProfileCell name={r.displayName} />,
  },
  {
    id: 'joined',
    header: 'Joined',
    width: '1fr',
    sortKey: 'joinedAt',
    cell: (r) => <span className="text-muted">{formatDate(r.joinedAt)}</span>,
  },
  {
    id: 'blueStar',
    header: 'Blue Star',
    width: '124px',
    cell: (r) => <StatusPill tone={BLUE_STAR_TONE[r.blueStarStatus]}>{BLUE_STAR_LABEL[r.blueStarStatus]}</StatusPill>,
  },
  {
    id: 'published',
    header: 'Published',
    width: '104px',
    align: 'right',
    numeric: true,
    sortKey: 'totalPublished',
    cell: (r) => formatCount(r.totalPublished),
  },
  {
    id: 'sold',
    header: 'Sold',
    width: '80px',
    align: 'right',
    numeric: true,
    sortKey: 'totalSold',
    cell: (r) => formatCount(r.totalSold),
  },
  {
    id: 'rating',
    header: 'Rating',
    width: '92px',
    sortKey: 'avgRating',
    cell: (r) => <RatingCell rating={r.avgRating} />,
  },
  {
    id: 'lastPublished',
    header: 'Last published',
    width: '1.25fr',
    sortKey: 'lastActiveAt',
    cell: (r) => <span className="text-muted">{formatDate(r.lastActiveAt)}</span>,
  },
  {
    id: 'status',
    header: 'Status',
    width: '120px',
    cell: (r) => (
      <StatusPill tone={CHEF_STATUS_TONE[r.status]} dot>
        {CHEF_STATUS_LABEL[r.status]}
      </StatusPill>
    ),
  },
];

const NEVER_COLUMNS: DataTableColumn<NeverPublished>[] = [
  {
    id: 'profile',
    header: 'Profile',
    width: '2fr',
    cell: (r) => <ProfileCell name={r.displayName} />,
  },
  {
    id: 'signup',
    header: 'Signed up',
    width: '1fr',
    cell: (r) => <span className="text-muted">{formatDate(r.signupAt)}</span>,
  },
  {
    id: 'days',
    header: 'Days since signup',
    width: '150px',
    align: 'right',
    numeric: true,
    cell: (r) => formatCount(Math.floor(r.hoursSinceSignup / 24)),
  },
];

/**
 * Chef directory (kit `ChefsScreen`; FE plan §7 Chefs). A chef is a consumer
 * profile with a published dish. Every filter, the sort, the page, the search
 * and the open dialog live in the URL.
 *
 * "No first dish" is a separate view: those profiles have no published
 * listing, so the directory's `hasPublishedListing=true` can never return
 * them. They come from the time-to-first-dish worklist instead (Q10).
 */
export function ChefsScreen() {
  const table = useTableUrlState();
  const { profile } = useSession();
  const f = table.filters;

  const view = f.view === NO_FIRST_DISH ? NO_FIRST_DISH : 'directory';
  const localeIds = listParam(f.localeIds);
  const signup = dayRangeParam(f.signupStart, f.signupEnd);
  const lastActive = dayRangeParam(f.lastActiveStart, f.lastActiveEnd);

  const filters: DirectoryFilters = {
    hasPublishedListing: true,
    demo: false,
    status: pickParam(f.status, CHEF_STATUSES),
    blueStarStatus: pickParam(f.blueStarStatus, BLUE_STAR_STATUSES),
    localeIds,
    searchTerm: table.q,
    signupStart: signup.start,
    signupEnd: signup.end,
    lastActiveStart: lastActive.start,
    lastActiveEnd: lastActive.end,
    ratingMin: numberParam(f.ratingMin, 1, 5),
    ratingMax: numberParam(f.ratingMax, 1, 5),
    sortBy: table.sort?.key as ProfileSortKey | undefined,
    order: table.sort?.dir,
  };

  const list = useProfileDirectory(
    { ...filters, page: table.page, pageSize: table.pageSize },
    { enabled: view !== NO_FIRST_DISH },
  );
  const exportCsv = useExportProfiles('chefs');

  const chefId = stringParam(f.chef);
  const chefTab = pickParam(f.chefTab, CHEF_TABS) ?? 'profile';
  const openChef = (id: string) => table.setParams({ chef: id, chefTab: undefined });

  const setView = (next: 'all' | (typeof CHEF_STATUSES)[number] | typeof NO_FIRST_DISH) => {
    if (next === NO_FIRST_DISH) table.setParams({ view: next, status: undefined, page: undefined });
    else table.setParams({ view: undefined, status: next === 'all' ? undefined : next, page: undefined });
  };
  const activeChip = view === 'directory' ? (filters.status ?? 'all') : view;

  return (
    <div className="flex flex-col gap-[18px]">
      <ChefKpis localeIds={localeIds} />

      <div>
        <FilterBar>
          <SelectableChip active={activeChip === 'all'} onClick={() => setView('all')}>
            All
          </SelectableChip>
          {CHEF_STATUSES.map((s) => (
            <SelectableChip key={s} active={activeChip === s} onClick={() => setView(s)}>
              {CHEF_STATUS_LABEL[s]}
            </SelectableChip>
          ))}
          <SelectableChip active={activeChip === NO_FIRST_DISH} onClick={() => setView(NO_FIRST_DISH)}>
            No first dish
          </SelectableChip>
          <FilterDivider />
          {view === 'directory' && (
            <Select
              aria-label="Filter by Blue Star"
              surface="white"
              compact
              style={{ width: 170 }}
              placeholder="Any Blue Star"
              value={filters.blueStarStatus ?? ''}
              options={BLUE_STAR_STATUSES.map((s) => ({ value: s, label: BLUE_STAR_LABEL[s] }))}
              onChange={(e) => table.setFilter('blueStarStatus', e.target.value || undefined)}
            />
          )}
          <LocalePicker value={localeIds} onChange={(ids) => table.setFilter('localeIds', ids.join(','))} />
          {view === 'directory' && (
            <div className="ml-auto">
              <ExportButton onExport={() => exportCsv.mutateAsync(filters).catch(() => undefined)} />
            </div>
          )}
        </FilterBar>

        {view === 'directory' ? (
          <>
            <FilterBar>
              <div className="flex items-center gap-2">
                <span className="text-[13px] font-semibold text-muted">Joined</span>
                <DateRangeFilter
                  value={signup}
                  timeZoneLabel={profile?.timezone}
                  onChange={(r) => table.setParams({ signupStart: r.start, signupEnd: r.end, page: undefined })}
                />
              </div>
              <FilterDivider />
              <div className="flex items-center gap-2">
                <span className="text-[13px] font-semibold text-muted">Last published</span>
                <DateRangeFilter
                  value={lastActive}
                  onChange={(r) => table.setParams({ lastActiveStart: r.start, lastActiveEnd: r.end, page: undefined })}
                />
              </div>
              <FilterDivider />
              <Select
                aria-label="Minimum rating"
                surface="white"
                compact
                style={{ width: 140 }}
                placeholder="Min rating"
                value={filters.ratingMin !== undefined ? String(filters.ratingMin) : ''}
                options={RATINGS.map((r) => ({ value: r, label: `${r}★ or more` }))}
                onChange={(e) => table.setFilter('ratingMin', e.target.value || undefined)}
              />
              <Select
                aria-label="Maximum rating"
                surface="white"
                compact
                style={{ width: 140 }}
                placeholder="Max rating"
                value={filters.ratingMax !== undefined ? String(filters.ratingMax) : ''}
                options={RATINGS.map((r) => ({ value: r, label: `${r}★ or less` }))}
                onChange={(e) => table.setFilter('ratingMax', e.target.value || undefined)}
              />
            </FilterBar>

            <DataTable
              label="Chefs"
              minWidth={1120}
              columns={COLUMNS}
              rows={list.data?.items ?? []}
              getRowId={(r) => r.profileId}
              onRowClick={(r) => openChef(r.profileId)}
              loading={list.isPending}
              error={list.error}
              onRetry={() => void list.refetch()}
              sort={table.sort}
              onSortChange={table.setSort}
              emptyLabel="No chefs match those filters."
            />
            <Pager
              page={table.page}
              pageSize={table.pageSize}
              total={list.data?.total ?? 0}
              onPageChange={table.setPage}
              onPageSizeChange={table.setPageSize}
              noun="chefs"
            />
          </>
        ) : (
          <NoFirstDish localeIds={localeIds} search={table.q} onOpen={openChef} />
        )}
      </div>

      {chefId && (
        <ChefDialog
          id={chefId}
          tab={chefTab}
          onTab={(t) => table.setParams({ chefTab: t === 'profile' ? undefined : t })}
          onClose={() => table.setParams({ chef: undefined, chefTab: undefined })}
        />
      )}
      <DetailDialogs />
    </div>
  );
}

/** KPI tiles via pageSize=1 count reads (until BE ask B10); they follow the locale filter. */
function ChefKpis({ localeIds }: { localeIds: string[] }) {
  const base: DirectoryFilters = { hasPublishedListing: true, demo: false, localeIds };
  const total = useProfileCount(base);
  const active = useProfileCount({ ...base, status: 'active' });
  const certified = useProfileCount({ ...base, blueStarStatus: 'approved' });
  const pending = useProfileCount({ ...base, blueStarStatus: 'pending' });
  const gone = useProfileCount({ ...base, status: 'gone' });
  const ofTotal = total.data !== undefined ? `of ${formatCount(total.data)}` : undefined;

  return (
    <MetricRow>
      <MetricCard
        label="Total chefs"
        value={formatCount(total.data)}
        hint="published at least once"
        accent="var(--admin-chart-1)"
        icon={<ChefHat size={15} aria-hidden="true" />}
      />
      <MetricCard
        label="Active"
        value={formatCount(active.data)}
        hint={ofTotal && `${ofTotal} · published recently`}
        accent="var(--admin-chart-3)"
        icon={<Flame size={15} aria-hidden="true" />}
      />
      <MetricCard
        label="Blue Star certified"
        value={formatCount(certified.data)}
        delta={pending.data ? `${formatCount(pending.data)} pending` : undefined}
        hint={pending.data ? 'needs approval' : undefined}
        accent="var(--admin-chart-2)"
        icon={<Award size={15} aria-hidden="true" />}
      />
      <MetricCard
        label="Gone"
        value={formatCount(gone.data)}
        hint={ofTotal && `${ofTotal} · stopped publishing`}
        accent="var(--admin-chart-4)"
        icon={<UserMinus size={15} aria-hidden="true" />}
      />
    </MetricRow>
  );
}

function NoFirstDish({
  localeIds,
  search,
  onOpen,
}: {
  localeIds: string[];
  search?: string;
  onOpen: (id: string) => void;
}) {
  const never = useNeverPublished(localeIds);
  const term = search?.trim().toLowerCase();
  const rows = useMemo(
    () => (never.data?.chefsNeverPublished ?? []).filter((r) => !term || r.displayName.toLowerCase().includes(term)),
    [never.data, term],
  );

  return (
    <>
      <DocNote>
        Consumer profiles that have never published a dish, oldest signup first. This is a worklist capped at 500, not
        an export; search matches display names in the list.
      </DocNote>
      <DataTable
        label="No first dish"
        minWidth={720}
        columns={NEVER_COLUMNS}
        rows={rows}
        getRowId={(r) => r.profileId}
        onRowClick={(r) => onOpen(r.profileId)}
        loading={never.isPending}
        error={never.error}
        onRetry={() => void never.refetch()}
        emptyLabel="Everyone here has published a dish."
      />
      {never.data && (
        <div className="mt-3.5 text-[13px] text-muted">
          Showing {formatCount(rows.length)} of {formatCount(never.data.chefsNeverPublished.length)}
        </div>
      )}
    </>
  );
}
